<template>
  <div
    class="d-flex user-facing-layout"
    :class="{ 'sidebar-open': isSidebarOpen }"
  >
    <!-- Mobile header that displays selected company -->
    <nav
      id="dashboard-mobile-header"
      class="navbar navbar-dark d-md-none overflow-y-auto bg-gray-900"
    >
      <div class="container-fluid justify-content-between">
        <span class="mb-0 company-name text-white">
          {{ selectedCompanyOrFirst?.name ?? $t("dashboard.select_company") }}
        </span>
        <button
          class="navbar-toggler"
          type="button"
          @click="() => toggleSidebar(true)"
        >
          <font-awesome-icon icon="caret-down" class="text-white" />
        </button>
      </div>
    </nav>
    <!-- Dashboard sidebar; contents come from the active portal -->
    <div
      id="dashboard-sidebar"
      class="bg-gray-1000 d-flex flex-column text-start flex-shrink-0 h-100 ps-3 pe-0 pt-3 pb-3"
    >
      <button
        type="button"
        class="btn-close btn-close-white d-md-none mb-2 align-self-end"
        aria-label="Close"
        @click="() => toggleSidebar(false)"
      />

      <component :is="portal.Sidenav" :accountData />
    </div>
    <div id="dashboard-wrapper" class="flex-grow-1">
      <RouterView />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAccount } from "@/composables/account/useAccount";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { getPortal } from "@/portals";
import type { Account, Document } from "@/types";
import { useToggle } from "@vueuse/core";
import { watch } from "vue";
import { useRoute } from "vue-router";

// Thin shell shared by every portal: mobile header, sidebar container, and
// the routed content area. What the sidebar contains is the active portal's
// business (see @/portals); this file must stay brand-agnostic.
const portal = getPortal();

const [isSidebarOpen, toggleSidebar] = useToggle(false);

const accountStore = useAccountStore();
const { accountData } = useAccount(refreshSignedDocuments);

const { selectedCompanyOrFirst } = useCompanyFinder(accountData);

const route = useRoute();

// When route changes, close the sidebar (especially important on mobile)
watch(
  () => route.fullPath,
  () => {
    if (isSidebarOpen.value) {
      toggleSidebar(false);
    }
  }
);

/**
 * Asks Dokobit about every document this account still has to sign. The status check records a
 * completed signature on the server, so the account is reloaded afterwards to show it — or the
 * whole page, when a signed document carries legal keys.
 */
async function refreshSignedDocuments(account: Readonly<Account>) {
  const awaitingSignature = account.companies
    .flatMap((company) => company.documents ?? [])
    .filter(
      (document) =>
        document.status !== "completed" &&
        document.dokobit_token &&
        document.document_signers?.some(
          (signer) => signer.account_id === account.id
        )
    );
  const signed: Readonly<Document>[] = [];
  for (const document of awaitingSignature) {
    if (await isSignedAtDokobit(document)) signed.push(document);
  }
  if (signed.some((document) => document.legal_keys?.length)) {
    location.reload();
  } else if (signed.length > 0) {
    await accountStore.reload();
  }
}

/** Whether Dokobit reports the document signed; a check that fails counts as not yet. */
async function isSignedAtDokobit(
  document: Readonly<Document>
): Promise<boolean> {
  try {
    const response = await goSolidApi.get(
      `/api/document-check-status?token=${document.dokobit_token}`
    );
    return response.status === 200 && response.data?.status === "completed";
  } catch (error: unknown) {
    console.error(
      `Error checking document status for token ${document.dokobit_token}:`,
      error
    );
    return false;
  }
}
</script>

<style scoped lang="scss">
.user-facing-layout {
  display: flex;
  flex-direction: row;
  overflow: hidden;
  flex-grow: 1;
  width: 100%;
  height: 100%;
  overscroll-behavior: none;
}

#dashboard-mobile-header {
  padding: 0.5rem 1rem;

  .company-name {
    font-weight: bold;
    font-size: 1.2rem;
  }
}

#dashboard-sidebar {
  max-width: 300px;
  overflow: hidden;
}

@media (min-width: 769px) {
  #dashboard-sidebar .btn-close {
    display: none;
  }
}

#dashboard-wrapper {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;
  width: 100%;
  background-color: var(--phoenix-gray-1000);
  height: 100%;
}

@media (max-width: 768px) {
  .user-facing-layout {
    flex-direction: column;
    height: 100%;
  }

  .user-facing-layout.sidebar-open #dashboard-sidebar {
    transform: translateX(0);
  }

  #dashboard-sidebar {
    max-width: none;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    height: 100vh;
    transform: translateX(-100%);
    transition: transform 0.3s ease-in-out;
  }

  #dashboard-wrapper {
    background-color: var(--phoenix-gray-800);
  }
}
</style>
